// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Json from "../primitives/Json.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Client from "@apollo/client";
import * as Primitive_float from "@rescript/runtime/lib/es6/Primitive_float.js";
import * as ApolloClient__Utilities from "rescript-apollo-client/src/@apollo/client/utilities/ApolloClient__Utilities.res.js";
import * as ApolloClient__ZenObservable from "rescript-apollo-client/src/zen-observable/ApolloClient__ZenObservable.res.js";
import * as ReasonMLCommunity__ApolloClient from "rescript-apollo-client/src/ReasonMLCommunity__ApolloClient.res.js";

let defaultTtl = 7 * 24 * 60 * 60 * 1000;

let defaultColdAfter = 60 * 60 * 1000;

function selectionName(selection) {
  switch (selection.kind) {
    case "Field" :
      let match = selection.name;
      let match$1 = selection.alias;
      if (match$1 !== undefined) {
        return match$1.value;
      } else if (match !== undefined) {
        return match.value;
      } else {
        return;
      }
    case "FragmentSpread" :
      let match$2 = selection.name;
      if (match$2 !== undefined) {
        return "..." + match$2.value;
      } else {
        return;
      }
    default:
      return;
  }
}

function key(document) {
  let match = ApolloClient__Utilities.getOperationDefinition(document);
  if (match === undefined) {
    return "anonymous()";
  }
  let name = Stdlib_Option.mapOr(match.name, "anonymous", param => param.value);
  let fields = Stdlib_Array.filterMap(match.selectionSet.selections, selectionName).join(",");
  return name + `(` + fields + `)`;
}

function makeStore(storageKeyOpt, maxSamplesOpt, maxEntriesOpt, ttlOpt, coldAfterOpt, nowOpt, param) {
  let storageKey = storageKeyOpt !== undefined ? storageKeyOpt : "operation-durations";
  let maxSamples = maxSamplesOpt !== undefined ? maxSamplesOpt : 10;
  let maxEntries = maxEntriesOpt !== undefined ? maxEntriesOpt : 50;
  let ttl = ttlOpt !== undefined ? ttlOpt : defaultTtl;
  let coldAfter = coldAfterOpt !== undefined ? coldAfterOpt : defaultColdAfter;
  let now = nowOpt !== undefined ? nowOpt : prim => Date.now();
  return {
    storageKey: storageKey,
    maxSamples: maxSamples,
    maxEntries: maxEntries,
    ttl: ttl,
    coldAfter: coldAfter,
    now: now,
    interactions: new Map(),
    persisted: undefined
  };
}

function validDuration(duration) {
  if (isFinite(duration) && duration >= 0) {
    return duration <= 300000;
  } else {
    return false;
  }
}

function expired(store, entry) {
  return store.now() - entry.updatedAt > store.ttl;
}

function decodeEntry(json, store) {
  let dict = Json.decodeDict(json);
  if (dict === undefined) {
    return;
  }
  let samples = Stdlib_Option.flatMap(Json.decodeDictFieldArray(dict, "samples"), items => {
    let samples = Stdlib_Array.filterMap(items, Json.decodeNumber);
    if (samples.length === items.length && samples.length !== 0 && samples.every(validDuration)) {
      return samples.slice(-store.maxSamples | 0);
    }
  });
  let match = Json.decodeDictFieldFloat(dict, "updatedAt");
  if (samples !== undefined && match !== undefined) {
    return {
      samples: samples,
      updatedAt: match
    };
  }
}

function decode(json, store) {
  let dict = Json.decodeDict(json);
  if (dict === undefined) {
    return;
  }
  let match = Json.decodeDictFieldFloat(dict, "version");
  let match$1 = Json.decodeDictField(dict, "entries", Json.decodeDict);
  if (match === undefined) {
    return;
  }
  if (match$1 === undefined) {
    return;
  }
  if (match !== 2) {
    return;
  }
  let entries = Stdlib_Array.filterMap(Object.entries(match$1), param => {
    let key = param[0];
    return Stdlib_Option.flatMap(decodeEntry(param[1], store), entry => {
      if (expired(store, entry)) {
        return;
      } else {
        return [
          key,
          entry
        ];
      }
    });
  }).toSorted((param, param$1) => Primitive_float.compare(param[1].updatedAt, param$1[1].updatedAt)).slice(-store.maxEntries | 0);
  return {
    entries: new Map(entries),
    lastActivityAt: Stdlib_Option.filter(Json.decodeDictFieldFloat(dict, "lastActivityAt"), prim => isFinite(prim))
  };
}

function encode(param) {
  let lastActivityAt = param.lastActivityAt;
  return Json.encodeDict(Object.fromEntries(Stdlib_Array.filterMap([
    [
      "version",
      Json.encodeNumber(2)
    ],
    Stdlib_Option.map(lastActivityAt, at => [
      "lastActivityAt",
      Json.encodeNumber(at)
    ]),
    [
      "entries",
      Json.encodeDict(Object.fromEntries(Array.from(param.entries.entries()).map(param => {
        let match = param[1];
        return [
          param[0],
          Json.encodeDict(Object.fromEntries([
            [
              "samples",
              Json.encodeArray(match.samples.map(Json.encodeNumber))
            ],
            [
              "updatedAt",
              Json.encodeNumber(match.updatedAt)
            ]
          ]))
        ];
      })))
    ]
  ], field => field)));
}

function remove(store) {
  try {
    localStorage.removeItem(store.storageKey);
    return;
  } catch (exn) {
    return;
  }
}

function load(store) {
  try {
    let raw = localStorage.getItem(store.storageKey);
    if (raw === null) {
      return {
        entries: new Map(),
        lastActivityAt: undefined
      };
    }
    let persisted = decode(Json.parseExn(raw), store);
    if (persisted !== undefined) {
      return persisted;
    } else {
      remove(store);
      return {
        entries: new Map(),
        lastActivityAt: undefined
      };
    }
  } catch (exn) {
    remove(store);
    return {
      entries: new Map(),
      lastActivityAt: undefined
    };
  }
}

function persisted(store) {
  let persisted$1 = store.persisted;
  if (persisted$1 !== undefined) {
    return persisted$1;
  }
  let persisted$2 = load(store);
  store.persisted = persisted$2;
  return persisted$2;
}

function save(store) {
  try {
    localStorage.setItem(store.storageKey, Json.stringify(encode(persisted(store))));
    return;
  } catch (exn) {
    return;
  }
}

function record(store, key, duration) {
  if (!validDuration(duration)) {
    return;
  }
  let match = persisted(store);
  let entries = match.entries;
  let samples = Stdlib_Option.mapOr(entries.get(key), [], param => param.samples).concat([Math.round(duration)]).slice(-store.maxSamples | 0);
  entries.delete(key);
  entries.set(key, {
    samples: samples,
    updatedAt: store.now()
  });
  let overflow = entries.size - store.maxEntries | 0;
  if (overflow > 0) {
    Array.from(entries.keys()).slice(0, overflow).forEach(key => {
      entries.delete(key);
    });
  }
  save(store);
}

function percentile(store, key, rank) {
  let entry = persisted(store).entries.get(key);
  if (entry === undefined) {
    return;
  }
  if (expired(store, entry)) {
    return;
  }
  let sorted = entry.samples.toSorted(Primitive_float.compare);
  let position = rank * (sorted.length - 1 | 0);
  let lowerIndex = Math.floor(position) | 0;
  let match = sorted[lowerIndex];
  let match$1 = sorted[lowerIndex + 1 | 0];
  if (match !== undefined) {
    if (match$1 !== undefined) {
      return match + (match$1 - match) * (position - lowerIndex);
    } else {
      return match;
    }
  }
}

function median(store, key) {
  return percentile(store, key, 0.5);
}

function interactionKey(key, interaction) {
  return key + `#` + interaction;
}

function coldKey(key) {
  return key + `#cold`;
}

let globalColdKey = "#cold";

function touch(store) {
  let persisted$1 = persisted(store);
  persisted$1.lastActivityAt = store.now();
  save(store);
}

function cold(store) {
  let lastActivityAt = persisted(store).lastActivityAt;
  if (lastActivityAt !== undefined) {
    return store.now() - lastActivityAt > store.coldAfter;
  } else {
    return true;
  }
}

function recordSettled(store, key, cold, duration) {
  if (cold) {
    record(store, coldKey(key), duration);
    return record(store, globalColdKey, duration);
  }
  let interaction = store.interactions.get(key);
  if (interaction !== undefined) {
    return record(store, interactionKey(key, interaction), duration);
  } else {
    return record(store, key, duration);
  }
}

let defaultStore = makeStore(undefined, undefined, undefined, undefined, undefined, undefined, undefined);

function estimate(storeOpt, defaultDurationOpt, interaction, operations) {
  let store = storeOpt !== undefined ? storeOpt : defaultStore;
  let defaultDuration = defaultDurationOpt !== undefined ? defaultDurationOpt : 1000;
  let cold$1 = cold(store);
  return Stdlib_Option.getOr(Stdlib_Array.reduce(operations, undefined, (estimate, operation) => {
    let key$1 = key(operation);
    let warm = Stdlib_Option.getOr(percentile(store, Stdlib_Option.mapOr(interaction, key$1, interaction => interactionKey(key$1, interaction)), 0.75), defaultDuration);
    let duration;
    if (cold$1) {
      let duration$1 = percentile(store, coldKey(key$1), 0.75);
      duration = duration$1 !== undefined ? duration$1 : Stdlib_Option.mapOr(percentile(store, globalColdKey, 0.75), warm, __x => Math.max(warm, __x));
    } else {
      duration = warm;
    }
    return Stdlib_Option.mapOr(estimate, duration, estimate => Math.max(estimate, duration));
  }), defaultDuration);
}

function setInteraction(storeOpt, operation, interaction) {
  let store = storeOpt !== undefined ? storeOpt : defaultStore;
  store.interactions.set(key(operation), interaction);
}

function clearInteraction(storeOpt, operation) {
  let store = storeOpt !== undefined ? storeOpt : defaultStore;
  store.interactions.delete(key(operation));
}

function makeLink(store, nowOpt) {
  let now = nowOpt !== undefined ? nowOpt : prim => performance.now();
  return ReasonMLCommunity__ApolloClient.Link.make((operation, forward) => {
    let match = ApolloClient__Utilities.getOperationDefinition(operation.query);
    let measured = match !== undefined ? match.operation === "query" : false;
    let key$1 = key(operation.query);
    let observable = new Client.Observable(observer => {
      let startedAt = now();
      let cold$1 = cold(store);
      let duration = {
        contents: undefined
      };
      let failed = {
        contents: false
      };
      let subscription = forward(operation).subscribe({
        start: undefined,
        next: result => {
          let errors = result.errors;
          let exit = 0;
          if (errors !== undefined && errors.length !== 0) {
            failed.contents = true;
          } else {
            exit = 1;
          }
          if (exit === 1) {
            if (Stdlib_Option.isNone(duration.contents)) {
              duration.contents = now() - startedAt;
            }
          }
          touch(store);
          observer.next(result);
        },
        error: error => {
          failed.contents = true;
          observer.error(error);
        },
        complete: () => {
          let match = failed.contents;
          let match$1 = duration.contents;
          if (measured && !(match || match$1 === undefined)) {
            recordSettled(store, key$1, cold$1, match$1);
          }
          observer.complete();
        }
      });
      return () => {
        subscription.unsubscribe();
      };
    });
    return ApolloClient__ZenObservable.Observable.fromJs(observable);
  });
}

let link = makeLink(defaultStore, undefined);

let defaultEstimatedDuration = 1000;

export {
  defaultEstimatedDuration,
  key,
  makeStore,
  record,
  percentile,
  median,
  estimate,
  setInteraction,
  clearInteraction,
  makeLink,
  link,
}
/* defaultStore Not a pure module */
